import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { DeleteSectionDto, FetchError, queryKeys, useAccessToken } from "src/http";
import { useSignalR } from "src/hooks";

type Variables = {
  planGuid: string;
  guid: string;
  modifiedByUserId: string;
};

const useDeleteSectionRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteSectionRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: DeleteSectionDto = {
      modifiedByUserId: variables.modifiedByUserId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/sections/${variables.guid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "DELETE",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteSectionRequest };
};

const useDeleteSection = () => {
  const { deleteSectionRequest } = useDeleteSectionRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteSectionRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables, _context) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getStructure(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useDeleteSection };
